'use client'

import { VaultSets } from '@/app/actions/user-vaults'
import { CoverImageProjectFragment, GetTokenCountPerWalletQuery } from '@/gql/generated/graphql'
import { cn } from '@/lib/utils'
import { AddressDisplay } from '@components/address-display'
import { Button } from '@components/button'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { LoadingIndicator } from '@components/loading-indicator'
import { SmoothBevelContainer } from '@components/smooth-bevel-container'
import { usePrivy } from '@privy-io/react-auth'
import { shallowEqual } from '@xstate/store'
import { useSelector } from '@xstate/store/react'
import { BodyText, DetailText } from 'design-configuration/components'
import { PlusIcon, ShieldIcon, WalletIcon } from 'design-configuration/components/icons'
import { AnimatePresence, motion } from 'motion/react'
import { useQuery } from 'urql'
import { Hex } from 'viem'
import { getTokenCountPerWalletDocument } from './gql/onboarding.graphql'
import { OnboardingStepHeader } from './onboarding-step-header'
import { SkipForNowButton } from './skip-for-now-button'
import { onboardingStore } from './store'

function CollectionStatsCell({
  label,
  value,
  className
}: {
  label: string
  value: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('border-outline-variant px-sm py-xs border-r last:border-r-0', className)}>
      <DetailText className="mb-xs text-on-surface-variant font-medium uppercase">{label}</DetailText>
      <BodyText className="font-medium">{value}</BodyText>
    </div>
  )
}

export function LinkAccountsStep({
  coverImageProjects
}: {
  coverImageProjects: CoverImageProjectFragment[]
}): React.ReactNode {
  const { linkWallet } = usePrivy()

  const { userAccounts, achievements, sets, tokens, delegations } = useSelector(
    onboardingStore,
    ({ context }) => {
      return {
        userAccounts: context.userAccounts,
        achievements: context.achievements,
        sets: context.sets,
        tokens: context.tokens,
        delegations: context.delegations
      }
    },
    shallowEqual
  )

  // TODO later: refactor this to go in the store.
  const allWalletAddresses = [...userAccounts, ...(delegations?.linkedVaults.map((vault) => vault.vault) ?? [])].map(
    (address) => address.toLowerCase()
  )
  const [res] = useQuery({
    query: getTokenCountPerWalletDocument,
    variables: {
      walletAddresses: allWalletAddresses
    }
  })
  const { data: tokenCountPerWallet, fetching: tokenCountPerWalletFetching } = res

  const userDataLoading = useSelector(onboardingStore, ({ context }) => {
    return context.userDataLoading
  })

  const totalTokens = tokens?.tokens_metadata_aggregate?.aggregate?.count ?? 0
  const setsInProgress = sets?.sets.filter((set) => set.progress > 0 && set.progress < 100).length ?? 0
  const achievementsToClaim = achievements?.filter((achievement) => !achievement.claimed).length ?? 0
  const linkedVaults = delegations?.linkedVaults

  // Generate a map of account to vaults, for nested display
  const accountDelegateMap = userAccounts.reduce<Map<string, VaultSets['linkedVaults']>>((map, account) => {
    const vaults =
      linkedVaults?.filter((delegation) => account.toLowerCase() === delegation.delegate.toLowerCase()) ?? []
    map.set(account, vaults)
    return map
  }, new Map())

  // If user has tokens available, show them in collection preview, otherwise show
  // random curated project tokens
  const userTokenImageUrls =
    (tokens?.tokens_metadata.filter((token) => token.image !== null) ?? []).map((token) => token.media_url ?? '') ?? []
  const coverImageTokenImageUrls = coverImageProjects.map((project) => project.featured_token?.[0]?.media_url ?? '')
  const showUserCollection = userTokenImageUrls.length > 0
  const NUM_DISPLAY_TOKENS = 4
  const displayTokens = (showUserCollection ? userTokenImageUrls : coverImageTokenImageUrls).slice(
    0,
    NUM_DISPLAY_TOKENS
  )

  const handleUnlinkVault = (vault: string) => {
    onboardingStore.trigger.unlinkVault({ vault })
  }

  const handleLinkVault = (vault: string) => {
    onboardingStore.trigger.linkVault({ vault })
  }

  return (
    <div className="flex min-h-full flex-col">
      <AnimatePresence mode="wait">
        {userDataLoading ? (
          <motion.div
            className="flex h-full flex-1 flex-col items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <LoadingIndicator className="fill-outline-variant h-10 w-10" />
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ visualDuration: 1 }}
            key="link-accounts"
            className="flex-1"
          >
            <div className="gap-md flex flex-col">
              <OnboardingStepHeader title="Your collecting journey so far" />
              <div>
                <div className="flex flex-wrap">
                  <div className="flex w-full flex-col gap-y-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3">
                      <CollectionStatsCell
                        label="Artworks"
                        value={`${totalTokens} total artworks`}
                        className="sm:pl-lg border-y"
                      />
                      <CollectionStatsCell
                        label="Sets"
                        value={`${setsInProgress} in progress`}
                        className="border-y border-r-0 sm:border-r"
                      />
                      <CollectionStatsCell
                        className="sm:pr-lg col-span-2 border-b sm:col-span-1 sm:border-y"
                        label="Achievements"
                        value={
                          <span className="gap-xs flex items-center">
                            {achievementsToClaim > 0 && (
                              <span
                                className="border-success-container h-xs w-xs animate-pulse rounded-full border"
                                style={{ background: 'var(--green-gradient)' }}
                              />
                            )}
                            <span>{achievementsToClaim} ready to claim</span>
                          </span>
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="bg-surface-container">
                  <div className="px-md py-xs">
                    <DetailText className="text-on-surface-variant">
                      {showUserCollection ? 'Your collection preview' : 'Your future collection awaits'}
                    </DetailText>
                  </div>
                  <div className="px-md pt-xs pb-sm gap-sm flex h-52">
                    {displayTokens.map((token, index) => (
                      <div key={index} className="relative flex-1">
                        <motion.div
                          className="absolute inset-0"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 + index * 0.01, ease: 'easeOut' }}
                        >
                          <ImageWithCustomLoader
                            src={token}
                            alt="Token"
                            className="object-contain"
                            fill
                            sizes="25vw"
                            style={{
                              filter: 'drop-shadow(3px 3px 6px rgba(0, 0, 0, 0.2))'
                            }}
                          />
                        </motion.div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="px-md">
                <div className="gap-sm mb-sm flex flex-col md:flex-row md:items-center">
                  <div className="flex-1">
                    <BodyText className="font-medium">Gather your collection across accounts</BodyText>
                    <DetailText className="text-on-surface-variant">
                      We&apos;ll automatically find pieces across all your addresses and vaults.
                    </DetailText>
                  </div>
                  <Button
                    colorTheme={'outline'}
                    iconLeft={<PlusIcon className="h-sm w-sm fill-on-surface mr-xs" />}
                    onClick={() => {
                      linkWallet?.()
                    }}
                    className="self-start md:self-auto"
                  >
                    Connect another wallet
                  </Button>
                </div>
                <div className="gap-sm flex flex-col">
                  {userAccounts.map((account) => {
                    const vaults = accountDelegateMap.get(account) ?? []
                    return (
                      <AccountCard
                        key={account}
                        account={account}
                        vaults={vaults}
                        onUnlinkVault={handleUnlinkVault}
                        onLinkVault={handleLinkVault}
                        tokenCounts={tokenCountPerWallet}
                        tokenCountsFetching={tokenCountPerWalletFetching}
                      />
                    )
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="p-md flex items-center justify-between">
        <Button colorTheme={'secondary'} branded onClick={() => onboardingStore.trigger.back()}>
          Back
        </Button>
        <div className="gap-md flex items-center">
          <SkipForNowButton onClick={() => onboardingStore.trigger.skipStep()} />
          <Button
            colorTheme={'primary'}
            branded
            onClick={() => {
              onboardingStore.trigger.linkAccountsStepCompleted()
            }}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}

function AccountCard({
  account,
  vaults,
  onUnlinkVault,
  onLinkVault,
  tokenCounts,
  tokenCountsFetching
}: {
  account: string
  vaults: VaultSets['linkedVaults']
  onUnlinkVault: (vault: string) => void
  onLinkVault: (vault: string) => void
  tokenCounts: GetTokenCountPerWalletQuery | undefined
  tokenCountsFetching: boolean
}) {
  const delegationsUpdating = useSelector(onboardingStore, ({ context }) => {
    return context.delegationsUpdating
  })

  return (
    <div>
      <SmoothBevelContainer key={account} className="mb-sm flex">
        <div className="flex flex-1 items-center">
          <WalletIcon className="h-sm w-sm fill-on-surface mr-xs" />
          <BodyText className="font-medium">
            <AddressDisplay address={account as Hex} />
          </BodyText>
        </div>
        <div className="relative left-[1ch] hidden flex-1 items-center sm:flex">
          <BodyText className={cn(tokenCountsFetching ? 'animate-pulse' : '')}>
            {tokenCounts?.users.find((user) => user.public_address.toLowerCase() === account.toLowerCase())
              ?.tokens_aggregate?.aggregate?.count ?? '--'}{' '}
            artworks found
          </BodyText>
        </div>
        {/* Spacer to align with the vaults */}
        <div className="flex-1" />
      </SmoothBevelContainer>
      {vaults.length > 0 ? (
        <div className="flex">
          <div className="w-sm flex justify-center">
            <div className="border-outline border-l" />
          </div>
          <div className="gap-sm flex flex-1 flex-col">
            {vaults.map((vault) => {
              const isUpdating = delegationsUpdating.includes(vault.vault)
              const renderButtonContent = () => {
                if (isUpdating) {
                  return <LoadingIndicator className="fill-outline-variant h-sm w-sm" />
                }

                if (vault.previouslyUnlinked) {
                  return (
                    <>
                      <span>Link</span>
                      <span className="hidden sm:inline"> to profile</span>
                    </>
                  )
                }

                return (
                  <>
                    <span>Unlink</span>
                    <span className="hidden sm:inline"> from profile</span>
                  </>
                )
              }

              return (
                <SmoothBevelContainer key={vault.vault} className="flex">
                  <div className="flex flex-1 items-center">
                    <ShieldIcon className="h-sm w-sm fill-on-surface mr-xs" />
                    <BodyText className="font-medium">
                      <AddressDisplay address={vault.vault as Hex} />
                    </BodyText>
                  </div>
                  <div className="hidden flex-1 items-center sm:flex">
                    <BodyText className={cn(tokenCountsFetching ? 'animate-pulse' : '')}>
                      {tokenCounts?.users.find(
                        (user) => user.public_address.toLowerCase() === vault.vault.toLowerCase()
                      )?.tokens_aggregate?.aggregate?.count ?? '--'}{' '}
                      artworks found
                    </BodyText>
                  </div>
                  <div className="flex flex-1 items-center justify-end">
                    <Button
                      colorTheme={'outline'}
                      onClick={() => {
                        if (isUpdating) {
                          return
                        }

                        if (vault.previouslyUnlinked) {
                          onLinkVault(vault.vault)
                        } else {
                          onUnlinkVault(vault.vault)
                        }
                      }}
                    >
                      {renderButtonContent()}
                    </Button>
                  </div>
                </SmoothBevelContainer>
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )
}
